import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import {
  makeStyles,
  Button,
  Typography,
  Grid,
  Switch,
  Box,
  Paper,
  TextField,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

import { selectItemSearchConfig } from '../app/appSlice';

import {
  MultiSelect,
  Select
} from '../common/fields';


const useStyles = makeStyles((theme) => ({
  searchFilters: {
    padding: theme.spacing(3),
    '& .MuiFilledInput-root': {
      backgroundColor: '#ffffff'
    }
  },
  row: {
    backgroundColor: theme.palette.background.sidebar,
    margin: theme.spacing(2,0,2,0),
    padding: '12px'
  },
  clearBtn: {
    marginRight: theme.spacing(2)
  },
  actions: {
    marginTop: theme.spacing(3)
  },
  newRowBtn: {
    textTransform: 'none',
    '& .MuiSvgIcon-root': {
      marginRight: '10px'
    }
  }
}));

const ItemSearchFilters = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const config = useSelector(selectItemSearchConfig);
  const { state, setState, clearState, handleSearch } = props;

  if(!config) {
    return null;
  }

  function addNewSearchRow() {
    const newFilter = {
      type: '', //multiChoice
      operation: '', //equals
      field: '', //t-code
      value: '', //T455456
      id: `search-row-${new Date().getTime()}`
    };

    setState({
      ...state,
      activeFilters: [
        ...state.activeFilters,
        newFilter,
      ],
    });
  }

  function clearSearch() {
    clearState();
  }

  const handleSwitchChange = event => {
    const searchOperator = event.target.checked ? 'OR' : 'AND';
    setState((state) => ({
      ...state,
      activeOperator: searchOperator
    }));
  };

  const handleFilterChange = rowId => event => {
    const oldRowState = state.activeFilters.find(row => row.id === rowId);

    let filterType = oldRowState.type;
    let filterOperation = oldRowState.operation;
    let filterValue = oldRowState.value;

    // Field defines which operators (and values for choices) are available
    if(event.target.name === 'field') {
      filterType = config.availableFields.find(item => item.id === event.target.value).filterType;
      const operations = config.typeOperations[filterType];

      filterOperation = operations[0];
      filterValue = filterType === 'multiChoice' ? [] : '';
    }

    const newState = {
      ...state,
      activeFilters: state.activeFilters.map(filterRow =>
        filterRow.id === rowId
          ? {
            ...filterRow,
            type: filterType,
            operation: filterOperation,
            value: filterValue,
            [event.target.name]: event.target.value
          }
          : filterRow
      )
    };

    setState(newState);
  };

  const handleFilterRemove = rowId => event => {
    const newState = {
      ...state,
      activeFilters: state.activeFilters.filter(filterRow => filterRow.id !== rowId)
    };

    setState(newState);
  };

  function renderRowValueControl(row) {
    const options = config.searchData[row.field] || [];

    if(row.type === 'multiChoice') {
      return (
        <MultiSelect
          label={t('itemSearch:value')}
          customLabel={false}
          variant="filled"
          name="value"
          value={row.value}
          options={options.map(item => ({label: item.name, value: item.code}))}
          onChange={handleFilterChange(row.id)}
          search
        />
      );
    } else if(row.type === 'choice') {
      return (
        <Select
          label={t('itemSearch:value')}
          customLabel={false}
          variant="filled"
          name="value"
          value={row.value}
          options={options.map(item => ({label: item.name, value: item.code}))}
          onChange={handleFilterChange(row.id)}
        />
      );
    } else {
      // Text field
      return (
        <TextField
          label={t('itemSearch:value')}
          name="value"
          value={row.value}
          onChange={handleFilterChange(row.id)}
          variant="filled"
          fullWidth
        />
        // <FilledInput id="item-search-value" name="value" value={row.value} onChange={handleFilterChange(row.id)} />
      );
    }

  }

  return (
    <div className={classes.searchFilters}>

      <Grid container alignItems="center" spacing={3}>
        <Grid item>
          <Typography variant="overline">{t('itemSearch:matchSearchCriteria')}</Typography>
        </Grid>

        <Grid item>
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>{t('itemSearch:all')}</Grid>
            <Grid item>
              <Switch inputProps={{ 'aria-label': t('itemSearch:matchSearchCriteria') }} color="primary" checked={state.activeOperator === 'OR'} onChange={handleSwitchChange} />
            </Grid>
            <Grid item>{t('itemSearch:any')}</Grid>
          </Grid>
        </Grid>
      </Grid>

      {state.activeFilters.map(row => {
        let fieldOperations = [];

        if(row.type) {
          fieldOperations = config.typeOperations[row.type] || [];
        }

        return (
          <Paper key={row.id} elevation={0} className={classes.row}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4} >
                <Select
                  label={t('itemSearch:field')}
                  customLabel={false}
                  variant="filled"
                  name="field"
                  value={row.field}
                  options={config.availableFields.map(item => ({label: t(`itemSearch:${item.id}`), value: item.id}))}
                  onChange={handleFilterChange(row.id)}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Select
                  label={t('itemSearch:operator')}
                  customLabel={false}
                  variant="filled"
                  name="operation"
                  value={row.operation}
                  options={fieldOperations.map(item => ({label: t(`itemSearch:${item}`), value: item}))}
                  onChange={handleFilterChange(row.id)}
                  disabled={!fieldOperations || !fieldOperations.length}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                {renderRowValueControl(row)}
              </Grid>

              <Grid item xs={12} sm={1} container justify="flex-end">
                <Button onClick={handleFilterRemove(row.id)} aria-label={t('itemSearch:removeRow')} title={t('itemSearch:removeRow')}>
                  <ClearIcon />
                </Button>
              </Grid>
            </Grid>
          </Paper>
        );
      })}


      <Button onClick={addNewSearchRow} className={classes.newRowBtn}>
        <AddCircleIcon color="primary" />
        <Typography variant="caption">
          {t('itemSearch:addRow')}
        </Typography>
      </Button>


      <Box mt={3}>
        <Grid
          container
          justify="flex-end"
          align="center"
        >
          <Box>
            <Button variant="outlined" color="primary" className={classes.clearBtn} onClick={clearSearch}>{t('itemSearch:clearSearch')}</Button>
            <Button variant="contained" color="primary" disableElevation={true} startIcon={<SearchIcon />} onClick={handleSearch}>{t('itemSearch:showResults')}</Button>
          </Box>
        </Grid>
      </Box>

    </div>
  );
};

ItemSearchFilters.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  state: PropTypes.object,
  setState: PropTypes.func,
  handleSearch: PropTypes.func,
  clearState: PropTypes.func
}

export default ItemSearchFilters;
